import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  useBreakpointValue
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Course Outcomes Section
 * What students will learn - key skills and competencies
 */
const CourseOutcomes = ({ data }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  if (!data) return null;

  const {
    title = "What You'll Learn",
    subtitle,
    description,
    outcomes = []
  } = data;

  if (outcomes.length === 0) return null;

  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={4} textAlign="center">
            <Heading as="h2" size="2xl" fontWeight="900" color="gray.900">
              {title}
            </Heading>
            {subtitle && (
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                {subtitle}
              </Text>
            )}
            {description && (
              <Text fontSize="md" color="gray.600" maxW="3xl">
                {description}
              </Text>
            )}
          </VStack>
        </MotionBox>

        {/* Outcomes Grid */}
        <SimpleGrid columns={gridCols} spacing={{ base: 6, md: 8 }}>
          {outcomes.map((outcome, idx) => (
            <MotionBox
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.6 }}
            >
              <VStack
                align="start"
                spacing={3}
                p={6}
                bg="gray.50"
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="brand.600"
                h="full"
              >
                <HStack spacing={3}>
                  <Icon as={CheckCircleIcon} color="brand.600" boxSize={6} flexShrink={0} />
                  <Heading as="h3" size="sm" color="gray.900">
                    {outcome.title}
                  </Heading>
                </HStack>
                {outcome.description && (
                  <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                    {outcome.description}
                  </Text>
                )}
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CourseOutcomes;
